import { useState, useEffect } from 'react';
import { Lock, LockOpen, Timer, LogOut, Search, Key, ChevronRight, ChevronDown, X, Plus, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useVault } from '@/features/vault/VaultContext';
import { VaultItemsList } from '@/features/vault/VaultItemsList';
import { VaultUnlockSheet } from '@/features/vault/VaultUnlockSheet';
import { AddItemSheet } from '@/features/vault/AddItemSheet';
import { SendListTab } from '@/features/vault/SendListTab';

export function VaultPage() {
  const {
    isUnlocked,
    lock,
    decryptedItems,
    collections,
    timeRemaining,
  } = useVault();

  const [showUnlock, setShowUnlock] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  const [searchVisibleFor, setSearchVisibleFor] = useState<Set<string>>(new Set());
  const [collectionSearches, setCollectionSearches] = useState<Record<string, string>>({});
  const [addingToCollection, setAddingToCollection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'passwords' | 'sends'>('passwords');

  // Close unlock sheet when vault is unlocked
  useEffect(() => {
    if (isUnlocked) {
      setShowUnlock(false);
    }
  }, [isUnlocked]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter collections by search query, then show all items in matching collections
  const query = searchQuery.trim().toLowerCase();

  // Find matching collection IDs
  const matchingCollectionIds = query
    ? collections
        .filter((c) => c.name.toLowerCase().includes(query))
        .map((c) => c.id)
    : null; // null = show all

  // Group items by collection (only matching collections if searching)
  const itemsByCollection = decryptedItems.reduce(
    (acc, item) => {
      const collectionId = item.collectionIds[0];
      const collection = collections.find((c) => c.id === collectionId);
      const collectionName = collection?.name || 'Без коллекции';

      // If searching, only include items from matching collections
      if (matchingCollectionIds !== null) {
        if (!matchingCollectionIds.includes(collectionId)) {
          return acc;
        }
      }

      if (!acc[collectionName]) {
        acc[collectionName] = { items: [], collectionId };
      }
      acc[collectionName].items.push(item);
      return acc;
    },
    {} as Record<string, { items: typeof decryptedItems; collectionId: string }>,
  );

  const hasResults = Object.keys(itemsByCollection).length > 0;

  const toggleExpanded = (collectionName: string) => {
    setExpandedCollections(prev => {
      const next = new Set(prev);
      if (next.has(collectionName)) {
        next.delete(collectionName);
      } else {
        next.add(collectionName);
      }
      return next;
    });
  };

  const toggleSearchVisible = (collectionName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchVisibleFor(prev => {
      const next = new Set(prev);
      if (next.has(collectionName)) {
        next.delete(collectionName);
        // Clear search when hiding
        setCollectionSearches(prev => ({ ...prev, [collectionName]: '' }));
      } else {
        next.add(collectionName);
        // Auto-expand when showing search
        setExpandedCollections(prev => new Set(prev).add(collectionName));
      }
      return next;
    });
  };

  const getFilteredItems = (items: typeof decryptedItems, collectionName: string) => {
    const localSearch = collectionSearches[collectionName]?.trim().toLowerCase();
    if (!localSearch) return items;
    return items.filter(item =>
      item.name?.toLowerCase().includes(localSearch) ||
      item.username?.toLowerCase().includes(localSearch) ||
      item.uris?.some(uri => uri.toLowerCase().includes(localSearch))
    );
  };

  const handleAddToCollection = (collectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setAddingToCollection(collectionId);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isUnlocked ? (
              <LockOpen className="h-5 w-5 text-green-500" />
            ) : (
              <Lock className="h-5 w-5" />
            )}
            <h1 className="text-lg font-semibold">Корпоративные пароли</h1>
          </div>

          {isUnlocked && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Timer className="h-4 w-4" />
                <span>{timeRemaining !== null ? formatTime(timeRemaining) : '--:--'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={lock}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Search - only when unlocked */}
        {isUnlocked && (
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по названию коллекции..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Locked state */}
        {!isUnlocked && (
          <div className="p-4 max-w-md mx-auto">
            <div className="text-center py-8">
              <Key className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-6">
                Разблокируйте хранилище для доступа к паролям
              </p>
              <Button onClick={() => setShowUnlock(true)}>
                <Lock className="h-4 w-4 mr-2" />
                Разблокировать
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Сессия автоматически завершится через 5 минут или при переключении вкладки
              </p>
            </div>
          </div>
        )}

        {/* Unlocked state */}
        {isUnlocked && (
          <>
            {/* Tabs */}
            <div className="flex border-b px-4">
              <button
                className={`flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors ${
                  activeTab === 'passwords' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
                }`}
                onClick={() => setActiveTab('passwords')}
              >
                Пароли
              </button>
              <button
                className={`flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors ${
                  activeTab === 'sends' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
                }`}
                onClick={() => setActiveTab('sends')}
              >
                <span className="inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Send-ы</span>
              </button>
            </div>

            {activeTab === 'sends' ? (
              <div className="p-4">
                <SendListTab />
              </div>
            ) : (
          <div className="p-4">
            {!hasResults ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'Коллекции не найдены' : 'Нет сохранённых паролей'}
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(itemsByCollection)
                  .sort(([a], [b]) => a.localeCompare(b, 'ru'))
                  .map(([collectionName, { items, collectionId }]) => {
                  const isExpanded = expandedCollections.has(collectionName);
                  const isSearchVisible = searchVisibleFor.has(collectionName);
                  const filteredItems = getFilteredItems(items, collectionName);

                  return (
                    <div key={collectionName} className="border rounded-lg overflow-hidden">
                      {/* Collection header */}
                      <div
                        className="flex items-center gap-2 px-3 py-2.5 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                        onClick={() => toggleExpanded(collectionName)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium flex-1 truncate">{collectionName}</span>
                        <span className="text-xs text-muted-foreground">{items.length}</span>
                        {items.length > 2 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 shrink-0 ${isSearchVisible ? 'bg-primary/10' : ''}`}
                            onClick={(e) => toggleSearchVisible(collectionName, e)}
                          >
                            <Search className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={(e) => handleAddToCollection(collectionId, e)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      {/* Collection content */}
                      {isExpanded && (
                        <div className="p-2">
                          {/* Search input */}
                          {isSearchVisible && (
                            <div className="relative mb-2">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                              <Input
                                value={collectionSearches[collectionName] || ''}
                                onChange={(e) => setCollectionSearches(prev => ({
                                  ...prev,
                                  [collectionName]: e.target.value
                                }))}
                                placeholder="Поиск в коллекции..."
                                className="h-8 pl-8 pr-8 text-sm"
                                onClick={(e) => e.stopPropagation()}
                              />
                              {collectionSearches[collectionName] && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="absolute right-0.5 top-1/2 -translate-y-1/2 h-7 w-7"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCollectionSearches(prev => ({ ...prev, [collectionName]: '' }));
                                  }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Items */}
                          {filteredItems.length === 0 ? (
                            <div className="py-3 text-center text-muted-foreground text-sm">
                              Ничего не найдено
                            </div>
                          ) : (
                            <VaultItemsList items={filteredItems} hideSearch />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
            )}
          </>
        )}
      </div>

      {/* Unlock sheet */}
      <VaultUnlockSheet open={showUnlock} onOpenChange={setShowUnlock} />

      {/* Add item sheet */}
      {addingToCollection && (
        <AddItemSheet
          collectionId={addingToCollection}
          open={!!addingToCollection}
          onOpenChange={(open) => !open && setAddingToCollection(null)}
        />
      )}
    </div>
  );
}
